// src/components/lead/sections/TrainingPanel.tsx
import { useEffect, useState } from "react";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import type { Location } from "../../../types/location";
import type { TrainingAnswer } from "../../../lib/training/types";
import { useSpeechDictation } from "../../../hooks/useSpeechDictation";
import type { ContactResult } from "../../../lib/contacts/types";
import { useAuth } from "@/contexts/AuthContext";
import { canHumanReviewRoleplay } from "@/lib/training/trainingReviewRoles";
import type { LocationResult } from "@/lib/contacts/router";
import { TrainingAskQueryControls } from "./TrainingAskQueryControls";
import { TrainingAskThreadTools } from "./TrainingAskThreadTools";
import { cardSurfaceClass, cardTitleTextClass } from "@/components/common/surfaceClasses";
import { TrainingPanelTopSection } from "./TrainingPanelTopSection";
import { TrainingAskResultCard } from "./TrainingAskResultCard";
import { useDevImpersonationStorage } from "@/hooks/useDevImpersonationStorage";
import { getOpsSettings } from "@/lib/opsPaperworkClient";
import type { KbLane } from "@/lib/kb/types";
import type { GuidanceProduct, GuidancePurpose, GuidanceSnapshot } from "./trainingPanelGuidance";
import { useTrainingAskFlow } from "./useTrainingAskFlow";
import { useTrainingGuidanceFilterHandlers } from "./useTrainingGuidanceFilterHandlers";
import { useTrainingPricingAskReset } from "./useTrainingPricingAskReset";

type SalespersonLike = { name: string } | null;

type TrainingPanelProps = {
  salesperson: SalespersonLike;
  location: Location | null;
  appVersion: string;
  webhookUrl?: string;
  kbQueryDebug?: (payload: any) => Promise<{ ok: boolean; status: number; json: any }>;
  // `scenarios` removed when training authoring re-homed to the
  // Manager-bucket `trainingAuthor` mode — see
  // docs/design/admin_split_and_entitlements.md §5 + §7.1.
  forcedTab?: "roleplay" | "ask" | "contacts" | "reviews" | null;
  onForcedTabApplied?: () => void;
};

const DEBUG_KB =
  String((import.meta as any)?.env?.VITE_DEBUG_KB || "").toLowerCase() === "true" ||
  String((import.meta as any)?.env?.VITE_DEBUG_KB || "") === "1";

export function TrainingPanel(props: TrainingPanelProps) {
  const { salesperson, location, appVersion, webhookUrl, kbQueryDebug, forcedTab, onForcedTabApplied } = props;
  const scopedOrganizationId = useScopedOrganizationId();
  const auth = useAuth();
  const devImp = useDevImpersonationStorage();
  const showReviewsTab = auth.status === "ready" && canHumanReviewRoleplay(auth.hasRole);
  const showPricingAsk = auth.status === "ready" && auth.hasRole("platform_owner", "platform_dev");

  const [query, setQuery] = useState("");
  const dictation = useSpeechDictation();
  const [lastQuery, setLastQuery] = useState("");
  const [result, setResult] = useState<TrainingAnswer | null>(null);
  const [resultQuery, setResultQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [needsClarification, setNeedsClarification] = useState(false);

  const [contacts, setContacts] = useState<ContactResult[]>([]);
  const [contactsWhy, setContactsWhy] = useState<"policy_support" | "direct" | "">("");
  const [contactsError, setContactsError] = useState<string | null>(null);
  const [locationCards, setLocationCards] = useState<LocationResult[]>([]);
  const [locationError, setLocationError] = useState<string | null>(null);
  const salespersonFirstName = salesperson?.name?.split(" ")[0];
  const locationId = location?.id?.trim() ? location.id : undefined;
  const kbLocationId = locationId ?? null;
  const locationName = location?.name;

  const [answerMode, setAnswerMode] = useState<"kb" | "contacts" | "location">("kb");
  const [guidanceOpen, setGuidanceOpen] = useState(false);
  const [laneHelpOpen, setLaneHelpOpen] = useState(false);
  const [laneSelection, setLaneSelection] = useState<KbLane>("answers");
  const [laneOverrideOnce, setLaneOverrideOnce] = useState<KbLane | null>(null);
  const [purposeSelection, setPurposeSelection] = useState<GuidancePurpose | null>(null);
  const [productSelection, setProductSelection] = useState<GuidanceProduct | null>(null);
  const [threadGuidanceById, setThreadGuidanceById] = useState<Record<string, GuidanceSnapshot>>({});
  const [activeGuidance, setActiveGuidance] = useState<GuidanceSnapshot | null>(null);
  const [guidanceNotice, setGuidanceNotice] = useState<string | null>(null);
  const [trainingTab, setTrainingTab] = useState<"roleplay" | "ask" | "contacts" | "reviews">("ask");
  const [contactsPresetQuery, setContactsPresetQuery] = useState<string | null>(null);
  const [askThreadId, setAskThreadId] = useState<string | null>(null);
  const [askThreadTurns, setAskThreadTurns] = useState<
    Array<{ question: string; answer: string; mode: "kb" | "contacts" | "location"; lane: KbLane }>
  >([]);
  const [nextAskKind, setNextAskKind] = useState<"new" | "followup">("new");
  const [threadFeedback, setThreadFeedback] = useState("");
  const [threadActionBusy, setThreadActionBusy] = useState<"note" | "escalate" | null>(null);
  const [threadActionMessage, setThreadActionMessage] = useState<{ tone: "ok" | "err"; text: string } | null>(null);
  const [threadToolsOpen, setThreadToolsOpen] = useState(false);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposeTitle, setProposeTitle] = useState("");
  const [proposeBody, setProposeBody] = useState("");
  const [proposeSubmitting, setProposeSubmitting] = useState(false);
  const [proposeMessage, setProposeMessage] = useState<{ tone: "ok" | "err"; text: string } | null>(null);
  const [thumbsDownOpen, setThumbsDownOpen] = useState(false);
  const [thumbsDownBusy, setThumbsDownBusy] = useState(false);
  const [thumbsDownReason, setThumbsDownReason] = useState<
    "" | "wrong_answer" | "missing_detail" | "outdated_policy" | "slang_uncommon_phrasing" | "wrong_contacts"
  >("");
  const [thumbsDownNotes, setThumbsDownNotes] = useState("");
  const [thumbsDownExpectedAnswer, setThumbsDownExpectedAnswer] = useState("");
  const [thumbsDownImpact, setThumbsDownImpact] = useState("");
  const [thumbsDownMessage, setThumbsDownMessage] = useState<{ tone: "ok" | "err"; text: string } | null>(null);
  const [kbTestingModeEnabled, setKbTestingModeEnabled] = useState(false);
  const canEscalateKbThread =
    auth.status === "ready" &&
    [
      "trainer",
      "admin",
      "owner",
      "dev",
      "platform_admin",
      "platform_dev",
      "platform_trainer",
    ].some((role) => auth.hasRole(role as any));

  useTrainingPricingAskReset({
    authStatus: auth.status,
    showPricingAsk,
    laneSelection,
    setLaneSelection,
    laneOverrideOnce,
    setLaneOverrideOnce,
    purposeSelection,
    setPurposeSelection,
  });

  const { onPurposeChange, onProductChange } = useTrainingGuidanceFilterHandlers({
    askThreadId,
    askThreadTurnsLength: askThreadTurns.length,
    setAskThreadId,
    setAskThreadTurns,
    setNextAskKind,
    setGuidanceNotice,
    setPurposeSelection,
    setProductSelection,
  });

  useEffect(() => {
    if (!forcedTab) return;
    if (forcedTab === "reviews" && !showReviewsTab) {
      onForcedTabApplied?.();
      return;
    }
    setTrainingTab(forcedTab);
    onForcedTabApplied?.();
  }, [forcedTab, onForcedTabApplied, showReviewsTab]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await getOpsSettings();
      if (!res.ok) return;
      const settings = (res.data?.settings ?? {}) as Record<string, unknown>;
      if (cancelled) return;
      setKbTestingModeEnabled(settings.kb_testing_mode_enabled === true);
    })();
    return () => {
      cancelled = true;
    };
  }, [scopedOrganizationId]);

  const { onAsk, onLaneSelectionChange, onThreadModeButtonClick } = useTrainingAskFlow({
    appVersion, webhookUrl, scopedOrganizationId, kbLocationId, locationId, locationName, salespersonFirstName, devImp, laneSelection, setLaneSelection, laneOverrideOnce, purposeSelection, productSelection, threadGuidanceById, askThreadTurns, askThreadId, nextAskKind,
    setAskThreadTurns, setAskThreadId, setContacts, setContactsWhy, setContactsError, setLocationCards, setLocationError, setLastQuery, setError, setLoading, setAnswerMode, setNeedsClarification, setResult, setResultQuery, setThreadGuidanceById, setActiveGuidance, setLaneOverrideOnce, setNextAskKind, setGuidanceNotice,
  });
  const hasAskThread = askThreadTurns.length > 0;

  const confirmDictationAsk = () => {
    const text = (dictation.draft || "").trim();
    if (!text) return;
    if (dictation.isListening) dictation.stop();
    setQuery(text);
    if (DEBUG_KB) {
      window.setTimeout(() => {
        if (!kbQueryDebug) return;
        void kbQueryDebug({
          query: text,
          audience: "any",
          topK: 5,
          locationId: kbLocationId ?? null,
          organizationId: scopedOrganizationId,
        });
      }, 0);
    }
    window.setTimeout(() => {
      void onAsk(text);
    }, 0);

    window.setTimeout(() => dictation.reset(), 0);
  };

  return (
    <div className="w-full text-left space-y-4">
      <TrainingPanelTopSection
        trainingTab={trainingTab}
        setTrainingTab={setTrainingTab}
        showReviewsTab={showReviewsTab}
        salespersonName={salesperson?.name}
        locationName={location?.name}
        scopedOrganizationId={scopedOrganizationId ?? ""}
        location={location}
        kbLocationId={kbLocationId}
        contactsPresetQuery={contactsPresetQuery}
        askPanel={(
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Ask a question</div>

        <TrainingAskQueryControls
          showPricingAsk={showPricingAsk}
          dictation={dictation}
          setResult={setResult}
          setError={setError}
          confirmDictationAsk={confirmDictationAsk}
          query={query}
          setQuery={setQuery}
          loading={loading}
          onAsk={() => onAsk(query)}
          laneSelection={laneSelection}
          onLaneSelectionChange={onLaneSelectionChange}
          laneHelpOpen={laneHelpOpen}
          setLaneHelpOpen={setLaneHelpOpen}
          hasAskThread={hasAskThread}
          nextAskKind={nextAskKind}
          onThreadModeButtonClick={onThreadModeButtonClick}
          guidanceOpen={guidanceOpen}
          setGuidanceOpen={setGuidanceOpen}
          purposeSelection={purposeSelection}
          productSelection={productSelection}
          onPurposeChange={onPurposeChange}
          onProductChange={onProductChange}
          laneOverrideOnce={laneOverrideOnce}
          setLaneOverrideOnce={setLaneOverrideOnce}
          guidanceNotice={guidanceNotice}
        />

        <TrainingAskThreadTools canEscalateKbThread={canEscalateKbThread} askThreadId={askThreadId} threadToolsOpen={threadToolsOpen} setThreadToolsOpen={setThreadToolsOpen} threadFeedback={threadFeedback} setThreadFeedback={setThreadFeedback} threadActionMessage={threadActionMessage} setThreadActionMessage={setThreadActionMessage} threadActionBusy={threadActionBusy} setThreadActionBusy={setThreadActionBusy} scopedOrganizationId={scopedOrganizationId ?? ""} />

        {error && (
          <div className={`mt-4 ${cardSurfaceClass} p-4`}>
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400 mb-2">Error</div>
            <div className={`text-sm ${cardTitleTextClass}`}>{error}</div>
          </div>
        )}

        {result && (
          <TrainingAskResultCard showPricingAsk={showPricingAsk} result={result} answerMode={answerMode} contacts={contacts} needsClarification={needsClarification} activeGuidance={activeGuidance} scopedOrganizationId={scopedOrganizationId ?? ""} resultQuery={resultQuery} lastQuery={lastQuery} query={query} appVersion={appVersion} webhookUrl={webhookUrl} locationId={locationId} locationName={locationName ?? ""} salespersonName={salesperson?.name} salespersonFirstName={salespersonFirstName} askThreadId={askThreadId} askThreadTurnsCount={askThreadTurns.length} nextAskKind={nextAskKind} canEscalateKbThread={canEscalateKbThread} kbTestingModeEnabled={kbTestingModeEnabled} proposeOpen={proposeOpen} setProposeOpen={setProposeOpen} proposeTitle={proposeTitle} setProposeTitle={setProposeTitle} proposeBody={proposeBody} setProposeBody={setProposeBody} proposeSubmitting={proposeSubmitting} setProposeSubmitting={setProposeSubmitting} proposeMessage={proposeMessage} setProposeMessage={setProposeMessage} thumbsDownOpen={thumbsDownOpen} setThumbsDownOpen={setThumbsDownOpen} thumbsDownBusy={thumbsDownBusy} setThumbsDownBusy={setThumbsDownBusy} thumbsDownReason={thumbsDownReason} setThumbsDownReason={setThumbsDownReason} thumbsDownNotes={thumbsDownNotes} setThumbsDownNotes={setThumbsDownNotes} thumbsDownExpectedAnswer={thumbsDownExpectedAnswer} setThumbsDownExpectedAnswer={setThumbsDownExpectedAnswer} thumbsDownImpact={thumbsDownImpact} setThumbsDownImpact={setThumbsDownImpact} thumbsDownMessage={thumbsDownMessage} setThumbsDownMessage={setThumbsDownMessage} locationCards={locationCards} locationError={locationError} contactsError={contactsError} contactsWhy={contactsWhy} onContactTagClick={(tag: string) => { setContactsPresetQuery(`tag:${tag}`); setTrainingTab("contacts"); }} />
        )}
      </div>
        )}
      />
    </div>
  );
}